import { shuffle } from '../rng/rng';
import { cardDefinitions, monsterGroupKeys, monsterGroupLevels } from '../cards/cardDefinitions.generated';
import { copyCounts } from '../cards/copyCounts.generated';
import { HIDDEN_SETS } from '../constants';

// See constants.js's HIDDEN_SETS - excludes a group only if EVERY card in it
// belongs to a hidden set (a group could in principle mix sets, e.g.
// buildCardData.mjs's own "guardian" group spans WC/RoC/ItA/Original - only
// fully-hidden groups should ever be dropped from selection).
const SELECTABLE_MONSTER_GROUP_KEYS = monsterGroupKeys.filter((group) => {
  const groupCards = cardDefinitions.filter((d) => d.type === 'monster' && d.group === group);
  return !groupCards.every((d) => HIDDEN_SETS.has(d.set));
});

// Picks one monster group per level (1, 2, 3) from the selectable ToR pool.
// Rulebook: "shuffle monster randomizers, reveal until you have one group each of
// level 1/2/3" (redraw duplicates) - equivalent to shuffling each level's pool
// independently and taking the top of each.
export function selectMonsterGroups(rngState) {
  const byLevel = { 1: [], 2: [], 3: [] };
  for (const group of SELECTABLE_MONSTER_GROUP_KEYS) byLevel[monsterGroupLevels[group]].push(group);

  let state = rngState;
  const selected = [];
  for (const level of [1, 2, 3]) {
    const [shuffled, next] = shuffle(byLevel[level], state);
    state = next;
    selected.push(shuffled[0]);
  }
  return [selected, state];
}

// Builds the dungeon deck: combine the 3 selected groups' 30 cards, shuffle,
// pull 10, shuffle the Thunderstone Bearer into those 10 to form the bottom-11
// stack, remaining 20 shuffled on top (rulebook "Building the Dungeon Deck").
// Returns [deckInstanceIds, nextRngState] where index 0 is the top of the deck.
export function buildDungeonDeck({ selectedGroups, bearerDefinitionId, makeInstance, rngState }) {
  const groupCardIds = [];
  for (const group of selectedGroups) {
    const defs = cardDefinitions.filter((d) => d.type === 'monster' && d.group === group);
    for (const def of defs) {
      const count = copyCounts[def.id].total;
      for (let i = 0; i < count; i += 1) groupCardIds.push(makeInstance(def.id));
    }
  }

  let state = rngState;
  const [shuffledThirty, afterFirstShuffle] = shuffle(groupCardIds, state);
  state = afterFirstShuffle;

  const bottomTen = shuffledThirty.slice(0, 10);
  const topTwenty = shuffledThirty.slice(10);

  const bearerInstanceId = makeInstance(bearerDefinitionId);
  const [bottomEleven, afterBottomShuffle] = shuffle([...bottomTen, bearerInstanceId], state);
  state = afterBottomShuffle;

  const [shuffledTopTwenty, afterTopShuffle] = shuffle(topTwenty, state);
  state = afterTopShuffle;

  const deck = [...shuffledTopTwenty, ...bottomEleven];
  return [deck, state];
}

// Matt's house rule: "When we set up the dungeon I always build a deck
// based on the total value of all the monster levels. I usually target 10
// total but you can adjust that total to adjust the length of the game.
// The way it works is I flip over a monster group randomizer and if they
// are a level 1 I add 1, a level 2 adds 2, a level 3 adds 3. I keep that
// running total until I reach or exceed my target value. That does mean
// that sometimes you have 8 or 9 and flip a 3 which means the total value
// is higher than 10 but that's ok. You include that group." Unlike
// selectMonsterGroups' "exactly 1 group per level" rule, this shuffles the
// WHOLE selectable pool together (mixed levels, no per-level partition) and
// walks it in that order, so the same level can repeat and the resulting
// group count varies game to game. Always includes at least 1 group.
// Returns [selectedGroupKeys, nextRngState].
export function selectMonsterGroupsByValue(rngState, targetValue) {
  const [shuffled, next] = shuffle(SELECTABLE_MONSTER_GROUP_KEYS, rngState);
  const selected = [];
  let runningTotal = 0;
  for (const group of shuffled) {
    selected.push(group);
    runningTotal += monsterGroupLevels[group];
    if (runningTotal >= targetValue) break;
  }
  return [selected, next];
}

// Matt's house rule: "I usually include multiple thunderstone bearers in my
// deck. I place one at the bottom of the deck and shuffle the rest into the
// deck. This should be a number I can choose." Also the deck-building path
// for selectMonsterGroupsByValue above, whose variable group count no
// longer fits buildDungeonDeck's fixed top-20/bottom-10 split built around
// exactly 30 cards - shuffles the WHOLE card pool together instead, no
// top/bottom structure of its own. `bearerDefinitionIds` is length 1 in the
// common case (still just one bearer, only routed here because the group
// count is variable) or length N under the multi-bearer rule - exactly one
// (the last in the array - already random, since callers pick this list via
// a shuffle-and-slice of the full bearer pool) is forced to the literal
// bottom of the deck (last array position - `deck[0]` is top/dealt-first,
// same convention `drawCards`'s own `deck.shift()` and buildDungeonDeck
// above rely on); the rest are shuffled in among the ordinary monster cards.
// Returns [deckInstanceIds, nextRngState].
export function buildFlexibleDungeonDeck({
  selectedGroups, bearerDefinitionIds, makeInstance, rngState,
}) {
  const groupCardIds = [];
  for (const group of selectedGroups) {
    const defs = cardDefinitions.filter((d) => d.type === 'monster' && d.group === group);
    for (const def of defs) {
      const count = copyCounts[def.id].total;
      for (let i = 0; i < count; i += 1) groupCardIds.push(makeInstance(def.id));
    }
  }

  const bearerInstanceIds = bearerDefinitionIds.map((defId) => makeInstance(defId));
  const bottomBearerInstanceId = bearerInstanceIds[bearerInstanceIds.length - 1];
  const shuffledInBearerIds = bearerInstanceIds.slice(0, -1);

  const [shuffledPool, next] = shuffle([...groupCardIds, ...shuffledInBearerIds], rngState);
  const deck = [...shuffledPool, bottomBearerInstanceId];
  return [deck, next];
}
